export interface TransferMetrics {
  transferredBytes: number;
  elapsedMs: number;
  bytesPerSecond: number | null;
  remainingSeconds: number | null;
  waiting: boolean;
}

/** Measure acknowledged bytes, not a resumed offset or time spent in the queue.
 * One meter per attempt; previous totals preserve the average across pauses.
 */
export function createTransferMeter(size: number, previous?: TransferMetrics, now = () => performance.now()) {
  let started: number | null = null;
  let stopped: number | null = null;
  let baseline = 0;
  let received = 0;
  let lastProgress = 0;
  const snapshot = (): TransferMetrics => {
    const time = stopped ?? now();
    const elapsedMs = (previous?.elapsedMs ?? 0) + (started === null ? 0 : Math.max(0, time - started));
    const transferredBytes = (previous?.transferredBytes ?? 0) + Math.max(0, received - baseline);
    const bytesPerSecond = elapsedMs >= 1000 && transferredBytes > 0 ? transferredBytes / (elapsedMs / 1000) : null;
    const waiting = started !== null && stopped === null && received < size && time - lastProgress >= 15000;
    return { transferredBytes, elapsedMs, bytesPerSecond, waiting,
      remainingSeconds: bytesPerSecond && started !== null && stopped === null && !waiting && received < size
        ? (size - received) / bytesPerSecond : null };
  };
  return {
    record(bytes: number) {
      if (stopped !== null) return snapshot();
      const time = now();
      if (started === null) { started = time; baseline = bytes; lastProgress = time; }
      if (bytes > received) lastProgress = time;
      received = bytes;
      if (received >= size) stopped = time;
      return snapshot();
    },
    snapshot,
    stop() { stopped ??= now(); return snapshot(); },
  };
}

export function displayTransferRate(bytesPerSecond: number) {
  const units = ['Б/с', 'КБ/с', 'МБ/с', 'ГБ/с'];
  const index = Math.min(units.length - 1, Math.max(0, Math.floor(Math.log2(Math.max(1, bytesPerSecond)) / 10)));
  return `${(bytesPerSecond / 1024 ** index).toLocaleString('ru-RU', { maximumFractionDigits: index ? 1 : 0 })} ${units[index]}`;
}

export function displayTransferTime(seconds: number) {
  const value = Math.max(1, Math.ceil(seconds));
  if (value < 60) return `${value} с`;
  const minutes = Math.floor(value / 60);
  if (minutes < 60) return `${minutes} мин${value % 60 ? ` ${value % 60} с` : ''}`;
  return `${Math.floor(minutes / 60)} ч${minutes % 60 ? ` ${minutes % 60} мин` : ''}`;
}
